import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Target, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle2,
  Clock,
  ChevronRight,
  Mountain,
  Users
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StrategyDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  const years = [2024, 2025, 2026, 2027];
  const quarters = [
    { value: null, label: 'All Quarters' },
    { value: 'Q1', label: 'Q1 (Jan-Mar)' },
    { value: 'Q2', label: 'Q2 (Apr-Jun)' },
    { value: 'Q3', label: 'Q3 (Jul-Sep)' },
    { value: 'Q4', label: 'Q4 (Oct-Dec)' },
  ];

  useEffect(() => {
    fetchDashboard();
  }, [selectedYear, selectedQuarter]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const params = { year: selectedYear };
      if (selectedQuarter) params.quarter = selectedQuarter;
      
      const response = await axios.get(`${API}/strategy/dashboard`, { params });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch strategy dashboard:', error);
      toast.error('Failed to load strategy dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'red': return 'bg-red-500';
      case 'yellow': return 'bg-yellow-500';
      case 'green': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      default: return 'bg-slate-300';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'red': return 'bg-red-50 border-red-200';
      case 'yellow': return 'bg-yellow-50 border-yellow-200';
      case 'green': return 'bg-green-50 border-green-200';
      case 'completed': return 'bg-blue-50 border-blue-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  if (loading) {
    return (
      <Layout>
        <Header title="Strategy Dashboard" />
        <div className="p-4 sm:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  const summary = dashboardData?.summary || {};
  const pillars = dashboardData?.pillars || [];
  const atRiskRocks = dashboardData?.at_risk_rocks || [];

  return (
    <Layout>
      <Header title="Strategy Dashboard" />
      
      <div className="p-4 sm:p-6 space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedQuarter || 'all'} onValueChange={(v) => setSelectedQuarter(v === 'all' ? null : v)}>
            <SelectTrigger className="w-44" data-testid="quarter-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {quarters.map(q => (
                <SelectItem key={q.value || 'all'} value={q.value || 'all'}>{q.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="ml-auto flex gap-2">
            <Link to="/strategy/svep">
              <Button variant="outline" size="sm">
                <Target className="w-4 h-4 mr-2" />
                Manage SVEP
              </Button>
            </Link>
            <Link to="/strategy/rocks">
              <Button size="sm">
                <Mountain className="w-4 h-4 mr-2" />
                View All Rocks
              </Button>
            </Link>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="total-rocks-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Rocks</p>
                  <p className="text-3xl font-bold text-slate-900">{summary.total_rocks || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mountain className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="avg-progress-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Progress</p>
                  <p className="text-3xl font-bold text-slate-900">{summary.avg_progress || 0}%</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <Progress value={summary.avg_progress || 0} className="mt-3 h-2" />
            </CardContent>
          </Card>
          
          <Card data-testid="on-track-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">On Track</p>
                  <p className="text-3xl font-bold text-green-600">{summary.on_track_percentage || 0}%</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="at-risk-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">At Risk</p>
                  <p className="text-3xl font-bold text-red-600">
                    {(summary.status_counts?.red || 0) + (summary.status_counts?.yellow || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {[
                { key: 'green', label: 'On Track', color: 'bg-green-500' },
                { key: 'yellow', label: 'At Risk', color: 'bg-yellow-500' },
                { key: 'red', label: 'Off Track', color: 'bg-red-500' },
                { key: 'completed', label: 'Completed', color: 'bg-blue-500' },
                { key: 'not_started', label: 'Not Started', color: 'bg-slate-300' },
              ].map(status => (
                <div key={status.key} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${status.color}`} />
                  <span className="text-sm text-slate-600">
                    {status.label}: <span className="font-semibold">{summary.status_counts?.[status.key] || 0}</span>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pillars Grid */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Progress by Strategic Pillar</h2>
          {pillars.length === 0 ? (
            <Card className="p-8 text-center">
              <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No strategic pillars defined yet</p>
              <Link to="/strategy/svep">
                <Button>
                  <Target className="w-4 h-4 mr-2" />
                  Create Your First Pillar
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pillars.map(pillar => (
                <Card key={pillar.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: pillar.color }}
                        />
                        <h3 className="font-semibold text-slate-900">{pillar.name}</h3>
                      </div>
                      <span className="text-sm text-slate-500">{pillar.rocks_count} rocks</span>
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-500">Progress</span>
                        <span className="font-medium">{pillar.avg_progress}%</span>
                      </div>
                      <Progress value={pillar.avg_progress} className="h-2" />
                    </div>
                    
                    <div className="flex gap-2 flex-wrap">
                      {pillar.status_counts?.green > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          {pillar.status_counts.green}
                        </span>
                      )}
                      {pillar.status_counts?.yellow > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                          <div className="w-2 h-2 rounded-full bg-yellow-500" />
                          {pillar.status_counts.yellow}
                        </span>
                      )}
                      {pillar.status_counts?.red > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                          {pillar.status_counts.red}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* At-Risk Rocks */}
        {atRiskRocks.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  At-Risk Rocks
                </CardTitle>
                <Link to="/strategy/rocks?status=red,yellow">
                  <Button variant="ghost" size="sm">
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {atRiskRocks.map(rock => (
                  <div 
                    key={rock.id}
                    className={`p-4 rounded-lg border ${getStatusBgColor(rock.status)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-2 h-2 rounded-full ${getStatusColor(rock.status)}`} />
                          <h4 className="font-medium text-slate-900">{rock.name}</h4>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {rock.owner_name}
                          </span>
                          <span 
                            className="px-2 py-0.5 rounded text-xs"
                            style={{ backgroundColor: rock.pillar_color + '20', color: rock.pillar_color }}
                          >
                            {rock.pillar_name}
                          </span>
                          <span>{rock.quarter} {rock.year}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold">{rock.percent_complete}%</p>
                        <p className="text-xs text-slate-500">complete</p>
                      </div>
                    </div>
                    {rock.risks_blockers && (
                      <p className="mt-2 text-sm text-slate-600 border-t border-slate-200 pt-2">
                        <span className="font-medium">Blockers:</span> {rock.risks_blockers}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default StrategyDashboard;
